import { ensureError } from '@sortera/ee-utils'
import type { AxiosInstance } from 'axios'
import axios from 'axios'

import { trackError } from '@/services/analytics.ts'
import { useAuth } from '@/services/use-auth.ts'
import {
  getFailedRequestsCount,
  incrementFailedRequestsCount,
  resetFailedRequestsCount,
} from '@/services/use-failed-requests-count.ts'

const FAILED_REQUESTS_RETRY_LIMIT = 3

export function createAxiosClient(): AxiosInstance {
  const axiosHttp = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 30000,
  })

  axiosHttp.interceptors.request.use(
    async config => {
      const auth = useAuth()
      // To prevent errors on hot reload and possibly in other scenarios, ensure auth is initialized
      await auth.ensureAuthenticatedUser()

      try {
        const token = await auth.getAccessToken()
        config.headers['Authorization'] = `Bearer ${token}`
      } catch (err) {
        const error = ensureError(err)
        console.error(error)
        trackError(error)
        incrementFailedRequestsCount()
        const shouldRetry = !didReachRetryLimit()

        if (shouldRetry) {
          console.error('Token retrieval failed, attempting login')
          await auth.login()
        } else {
          console.error('Failed to authorize after multiple attempts')
        }
      }
      return config
    },
    error => Promise.reject(error)
  )

  axiosHttp.interceptors.response.use(
    // success
    response => {
      resetFailedRequestsCount()
      return response
    },
    // failure
    async error => {
      if (error.status === 401) {
        incrementFailedRequestsCount()
        const shouldRetry = !didReachRetryLimit()

        if (shouldRetry) {
          console.error('Unauthorized, attempting login')
          await useAuth().login()
        } else {
          console.error('Failed to authorize after multiple attempts')
        }
      }
      return Promise.reject(error)
    }
  )

  return axiosHttp
}

function didReachRetryLimit(): boolean {
  const failedRequests = getFailedRequestsCount()
  return failedRequests >= FAILED_REQUESTS_RETRY_LIMIT
}
